import { useState } from "react";
import { ClipboardCopy, Table, LogOut, User } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { SignInForm } from "../auth/SignInForm";
import { InviterForm } from "./InviterForm";
import { RegistrationViewer } from "./RegistrationViewer";

type AdminTab = "generator" | "viewer";

/**
 * AdminPage — protected admin portal with URL generator and registration viewer.
 * Requires Cognito authentication (Requirement 8).
 */
export function AdminPage() {
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<AdminTab>("generator");

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted">Loading…</p>
      </div>
    );
  }

  // Not authenticated → show in-app sign-in form (no Hosted UI redirect, works in China)
  if (!isAuthenticated) {
    return <SignInForm />;
  }

  // Authenticated → show admin portal
  return (
    <div className="min-h-screen bg-background">
      {/* Header with user info and sign out (Requirements 8.4, 8.5) */}
      <header className="border-b border-border bg-surface">
        <div className="mx-auto max-w-6xl px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ClipboardCopy
              className="h-6 w-6 text-primary"
              aria-hidden="true"
            />
            <h1 className="text-xl font-semibold text-foreground">
              管理后台
            </h1>
          </div>
          <div className="flex items-center gap-4">
            {/* User identity (Requirement 8.4) */}
            <span className="flex items-center gap-1.5 text-sm text-muted">
              <User className="h-4 w-4" aria-hidden="true" />
              {user?.email}
            </span>
            {/* Sign out (Requirement 8.5) */}
            <button
              type="button"
              onClick={logout}
              className="cursor-pointer inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-1.5 text-sm text-muted hover:text-foreground hover:border-primary-light transition-colors duration-200"
            >
              <LogOut className="h-4 w-4" aria-hidden="true" />
              退出
            </button>
          </div>
        </div>
      </header>

      {/* Tab navigation */}
      <nav className="border-b border-border bg-surface">
        <div className="mx-auto max-w-6xl px-6 flex gap-1">
          <TabButton
            active={activeTab === "generator"}
            onClick={() => setActiveTab("generator")}
            icon={<ClipboardCopy className="h-4 w-4" aria-hidden="true" />}
            label="链接生成"
          />
          <TabButton
            active={activeTab === "viewer"}
            onClick={() => setActiveTab("viewer")}
            icon={<Table className="h-4 w-4" aria-hidden="true" />}
            label="报名数据"
          />
        </div>
      </nav>

      {/* Content */}
      <main className="mx-auto max-w-6xl px-6 py-8">
        {activeTab === "generator" && <InviterForm />}
        {activeTab === "viewer" && <RegistrationViewer />}
      </main>
    </div>
  );
}

// ─── Tab Button ─────────────────────────────────────────────────────────────

function TabButton({
  active,
  onClick,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors duration-200 ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted hover:text-foreground hover:border-border"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}
